import { CommonModule } from '@angular/common';
import { Component, computed, inject, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { ReactiveFormsModule } from '@angular/forms';
import {
  applyWhenValue,
  email,
  form,
  FormField,
  maxLength,
  minLength,
  pattern,
  required,
  validate,
} from '@angular/forms/signals';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { NzAvatarModule } from 'ng-zorro-antd/avatar';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzCardModule } from 'ng-zorro-antd/card';
import { NzCheckboxModule } from 'ng-zorro-antd/checkbox';
import { NzDividerModule } from 'ng-zorro-antd/divider';
import { NzDropdownModule } from 'ng-zorro-antd/dropdown';
import { NzFlexModule } from 'ng-zorro-antd/flex';
import { NzFormModule } from 'ng-zorro-antd/form';
import { NzGridModule } from 'ng-zorro-antd/grid';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzMessageService } from 'ng-zorro-antd/message';
import { NzSelectModule } from 'ng-zorro-antd/select';
import { NzSpinComponent } from 'ng-zorro-antd/spin';
import { NzTagModule } from 'ng-zorro-antd/tag';
import { map } from 'rxjs';
import { UserCreateRequest } from '../../../../core/api/mhsIdentityApiClient';
import { PhonePrefixLookup } from '../../../../core/context/phone-prefix-lookup';
import { RolesLookup } from '../../../../core/context/roles-lookup';
import { UsertStore } from '../../../../core/context/userStore';
import { CompanyConfigurationService } from '../../../../core/services/company-configuration-service';
import { ResourceService } from '../../../../core/services/resource-service';
import { nullIfEmpty } from '../../../../core/utils/transformers';
import { FieldErrorsComponent } from '../../../../shared/ui/components/field-error/field-error';
import { UserService } from '../../services/user-service';
import { UserData } from './user-data';
@Component({
  selector: 'app-user-create',
  imports: [
    ReactiveFormsModule,
    FormField,
    NzCardModule,
    NzFormModule,
    NzInputModule,
    NzButtonModule,
    NzSelectModule,
    NzSpinComponent,
    NzCheckboxModule,
    RouterLink,
    NzFlexModule,
    NzGridModule,
    NzDividerModule,
    NzIconModule,
    NzTagModule,
    CommonModule,
    NzAvatarModule,
    NzDropdownModule,
    FieldErrorsComponent,
  ],
  templateUrl: './user-create.html',
  styleUrl: './user-create.scss',
})
export class UserCreateComponent {
  readonly medicalRoleNames = ['MEDICAL', 'TECHNICIAN', 'NURSE', 'MEDICAL_PRACTICIONER'];
  readonly roles = RolesLookup.roles;
  showPassword = false;
  showPasswordConfirm = false;
  userService = inject(UserService);
  userStore = inject(UsertStore);
  resourceService = inject(ResourceService);
  route = inject(ActivatedRoute);
  router = inject(Router);
  messageService = inject(NzMessageService);

  companyConfigurationService = inject(CompanyConfigurationService);
  companyConfiguration = toSignal(this.companyConfigurationService.getCompanyConfiguration(), {
    initialValue: null,
  });

  phonePrefixes = PhonePrefixLookup.phonePrefixes;
  isSubmitting = signal(false);
  submitted = signal(false);
  selectAllUnits = signal<boolean>(false);
  selectAllModules = signal<boolean>(false);
  identifierTypes = toSignal(
    this.resourceService
      .getResourceIdentityTypes()
      .pipe(map((types) => types.filter((type) => type.name == 'PESEL' || type.name == 'OTHER'))),
    { initialValue: [] },
  );
  medTitles = toSignal(this.resourceService.getMedTitles(), { initialValue: [] });
  medSpecializations = toSignal(this.resourceService.getMedSpecializations(), { initialValue: [] });
  medRoleIds = computed(() => {
    return (
      this.companyConfiguration()
        ?.avilableRoles?.filter((role) => this.medicalRoleNames.includes(role.normalizedName!))
        .map((role) => role.id) || []
    );
  });

  userCreateModel = signal<UserData>({
    login: '',
    email: '',
    password: '',
    passwordConfirm: '',
    roleId: 1,
    selecteUnitIds: [1],
    firstName: '',
    secondName: '',
    lastName: '',
    identifierType: 'PESEL',
    identifierValue: '',
    phoneNumber: '',
    phoneNumberCountryCode: '+48',

    licenseNumber: '',
    medTitle: '',
    medSpecialty: '',
    defaultUnitId: this.companyConfiguration()?.structure?.units?.[0]?.id ?? 0,
    selectedModules: ['basic'],
    applicationAccess: ['mhs.scm.user'],
    selectedRoles: ['mhs.scm.user'],
  });

  unitRows = computed(() => {
    const units = this.companyConfiguration()?.structure?.units ?? [];
    const rows: any[][] = [];
    for (let i = 0; i < units.length; i += 3) {
      if (i + 3 > units.length) {
        rows.push(units.slice(i, units.length));
        break;
      }
      rows.push(units.slice(i, i + 3));
    }
    return rows;
  });

  loginForm = form(this.userCreateModel, (userModel) => {
    required(userModel.login);
    minLength(userModel.login, 3, { message: 'Login powinien mieć przynajmniej 3 znaki' });
    maxLength(userModel.login, 20, { message: 'Login powinien mieć co najwyżej 20 znaków' });
    email(userModel.email);
    required(userModel.email);
    required(userModel.password);

    pattern(userModel.password, /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^A-Za-z\d]).{8,}$/, {
      message:
        'Hasło musi mieć co najmniej 8 znaków oraz zawierać małą literę, dużą literę, cyfrę i znak specjalny.',
    });
    required(userModel.passwordConfirm);
    pattern(userModel.passwordConfirm, /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^A-Za-z\d]).{8,}$/, {
      message:
        'Hasło musi mieć co najmniej 8 znaków oraz zawierać małą literę, dużą literę, cyfrę i znak specjalny.',
    });
    required(userModel.firstName);
    required(userModel.lastName);
    required(userModel.identifierType);
    required(userModel.identifierValue);
    required(userModel.phoneNumber);
    pattern(userModel.phoneNumber, /^[0-9]+$/, {
      message: 'Pole może zawierać wyłącznie cyfry',
    });
    minLength(userModel.phoneNumber, 9, { message: 'Numer telefonu powinien mieć 9 znaków' });
    maxLength(userModel.phoneNumber, 9, { message: 'Numer telefonu powinien mieć 9 znaków' });
    required(userModel.phoneNumberCountryCode);
    minLength(userModel.selecteUnitIds, 1, {
      message: 'Wybierz przynajmniej jedną placówkę',
    });
    required(userModel.licenseNumber, {
      message: 'Numer prawa wykonywania zawodu jest wymagany dla ról medycznych',
      when: ({ valueOf }) => this.medRoleIds().includes(valueOf(userModel.roleId)),
    });
    applyWhenValue(
      userModel,
      (v) => this.medRoleIds().includes(v.roleId),
      (u) => {
        minLength(u.licenseNumber, 7, {
          message: 'Numer prawa wykonywania zawodu powinien mieć przynajmniej 7 znaków',
        });
        maxLength(u.licenseNumber, 12, {
          message: 'Numer prawa wykonywania zawodu powinien mieć maksymalnie 12 znaków',
        });
      },
    );
    applyWhenValue(
      userModel,
      (v) => v.identifierType === 'PESEL',
      (u) => {
        minLength(u.identifierValue, 11, { message: 'PESEL powinien mieć 11 znaków' });
        maxLength(u.identifierValue, 11, { message: 'PESEL powinien mieć 11 znaków' });
        pattern(u.identifierValue, /^[0-9]+$/, {
          message: 'Pole może zawierać wyłącznie cyfry',
        });
      },
    );
    applyWhenValue(
      userModel,
      (v) => v.identifierType === 'OTHER',
      (u) => {
        minLength(u.identifierValue, 5, {
          message: 'Wartość identyfikatora powinna mieć przynajmniej 5 znaków',
        });
        maxLength(u.identifierValue, 20, {
          message: 'Wartość identyfikatora powinna mieć co najwyżej 20 znaków',
        });
      },
    );
    validate(userModel.passwordConfirm, ({ value, valueOf }) => {
      const passwordConfirm = value();
      const password = valueOf(userModel.password);

      if (password === passwordConfirm) {
        return null;
      }
      return {
        kind: 'passwordMismatch',
        message: 'Hasła muszą być takie same',
      };
    });
  });

  unitCheckboxToggle(unitId: number, checked: boolean) {
    const selectedUnitIds = this.loginForm.selecteUnitIds().value();
    if (checked) {
      const newSelectedUnitIds = [...selectedUnitIds, unitId];
      this.userCreateModel.update((model) => ({ ...model, selecteUnitIds: newSelectedUnitIds }));
    } else {
      const filteredUnitIds = selectedUnitIds.filter((id) => id !== unitId);
      this.userCreateModel.update((model) => ({ ...model, selecteUnitIds: filteredUnitIds }));
    }
  }
  toggleSelectAllUnits() {
    const newValue = !this.selectAllUnits();
    this.selectAllUnits.set(newValue);
    if (newValue) {
      const allUnitIds =
        this.companyConfiguration()?.structure?.units?.map((unit) => unit.id!) || [];
      this.userCreateModel.update((model) => ({ ...model, selecteUnitIds: allUnitIds }));
    } else {
      this.userCreateModel.update((model) => ({ ...model, selecteUnitIds: [] }));
    }
  }
  toggleSelectAllModules() {
    const newValue = !this.selectAllModules();
    this.selectAllModules.set(newValue);
    if (newValue) {
      const allModuleNames =
        this.companyConfiguration()?.avilableModules?.map((module) => module.name!) || [];
      this.userCreateModel.update((model) => ({ ...model, selectedModules: allModuleNames }));
    } else {
      this.userCreateModel.update((model) => ({ ...model, selectedModules: [] }));
    }
  }
  moduleCheckboxToggle(moduleName: string, checked: boolean) {
    const selectedModules = this.loginForm.selectedModules().value();
    if (checked) {
      const newSelectedModules = [...selectedModules, moduleName];
      this.userCreateModel.update((model) => ({
        ...model,
        selectedModules: newSelectedModules,
      }));
    } else {
      const filteredModules = selectedModules.filter((name) => name !== moduleName);
      this.userCreateModel.update((model) => ({ ...model, selectedModules: filteredModules }));
    }
  }
  roleCheckboxToggle(roleName: string, checked: boolean) {
    const selectedRoles = this.loginForm.selectedRoles().value();
    if (checked) {
      const newSelectedRoles = [...selectedRoles, roleName];
      this.userCreateModel.update((model) => ({
        ...model,
        selectedRoles: newSelectedRoles,
      }));
    } else {
      const filteredRoles = selectedRoles.filter((name) => name !== roleName);
      this.userCreateModel.update((model) => ({ ...model, selectedRoles: filteredRoles }));
    }
  }

  setMedSpecialization(specialization: string) {
    this.userCreateModel.update((model) => ({ ...model, medSpecialty: specialization }));
  }
  markAllTouchedAndDirty(node: any) {
    // 1. oznacz tylko jeśli to field
    node.markAsTouched?.();
    node.markAsDirty?.();

    // 2. dzieci mogą być w różnych miejscach (Signal Forms nie ma jednego standardu)
    const children = node.controls ?? node.fields ?? node.children ?? null;

    if (!children) return;

    Object.values(children).forEach((child: any) => {
      this.markAllTouchedAndDirty(child);
    });
  }

  mapToApiModel(userData: UserData): UserCreateRequest {
    const request = new UserCreateRequest();
    const roleName = this.companyConfiguration()?.avilableRoles?.find(
      (role) => role.id === userData.roleId,
    )?.name;
    const currentTennt = this.userStore.currentTenant();
    request.login = userData.login;
    request.password = userData.password;
    request.roleId = userData.roleId;
    request.unitIds = userData.selecteUnitIds;
    request.tenants = [currentTennt?.tenantName!];
    request.firstName = userData.firstName;
    request.secondName = nullIfEmpty(userData.secondName) ?? undefined;
    request.lastName = userData.lastName;
    request.email = userData.email;
    request.phone = `${userData.phoneNumberCountryCode}${userData.phoneNumber}`;
    request.defaultUnitId = userData.defaultUnitId;
    request.identifierType = userData.identifierType as any;
    request.identifierValue = userData.identifierValue;
    request.title = nullIfEmpty(userData.medTitle) ?? undefined;
    request.licenceNumber = nullIfEmpty(userData.licenseNumber) ?? undefined;
    request.specialisation = nullIfEmpty(userData.medSpecialty) ?? undefined;
    request.internalRoles = userData.selectedRoles;
    request.modules = userData.selectedModules;

    request.roleName = nullIfEmpty(roleName) ?? undefined;

    return request;
  }

  submit() {
    this.submitted.set(true);
    this.markAllTouchedAndDirty(this.loginForm());

    if (this.loginForm().valid()) {
      this.isSubmitting.set(true);
      this.userService
        .create(this.mapToApiModel(this.userCreateModel()))
        .subscribe(
          (result) => {
            this.messageService.success('Użytkownik został utworzony');
            this.router.navigate(['/users']);
          },
          (error) => {
            console.error('Error creating user:', error);
          },
        )
        .add(() => {
          this.isSubmitting.set(false);
        });
    } else {
      this.isSubmitting.set(false);
    }
  }
}
